import { FunctionComponent, useState, useMemo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { CreateKidDocumentValues } from '../CreateKidDocumentModal';
import GenericDropdown from '../../../../../ui/GenericDropDown';

interface DuplicateDocumentSelectorProps {
  fundsMap: Map<string, any>;
  shareClassesByFund: Map<string, any[]>;
  uniqueFundIdsFromFunds: Set<string>;
  orderedFundIds: string[];
  allShareClasses: any[] | undefined;
}

const DuplicateDocumentSelector: FunctionComponent<DuplicateDocumentSelectorProps> = ({
  fundsMap,
  shareClassesByFund,
  uniqueFundIdsFromFunds,
  orderedFundIds,
  allShareClasses,
}) => {
  const { watch, setValue, formState: { errors } } = useFormContext<CreateKidDocumentValues>();
  const template_share_class_id = watch('template_share_class_id');
  const [selectedFundId, setSelectedFundId] = useState<string>('');


  // Sync selectedFundId with template_share_class_id from form state
  useEffect(() => {
    if (!template_share_class_id) {
      setSelectedFundId('');
      setValue('template_fund_name', undefined, { shouldDirty: false });
      setValue('template_fund_id', undefined, { shouldDirty: false });
    } else if (allShareClasses) {
      const shareClass = allShareClasses.find(
        (sc: any) => sc.share_class_id === template_share_class_id,
      );
      if (shareClass && shareClass.fund_id !== selectedFundId) {
        setSelectedFundId(shareClass.fund_id);
      }
      if (shareClass) {
        setValue('template_fund_name', fundsMap.get(shareClass.fund_id)?.fund_name_full, { shouldDirty: false });
        setValue('template_fund_id', shareClass.fund_id, { shouldDirty: false });
      }
    }

  }, [template_share_class_id, allShareClasses, setValue, fundsMap]);

  // Funds with share classes for dropdown
  const fundsWithShareClasses = useMemo(
    () =>
      (orderedFundIds.length > 0
        ? orderedFundIds.filter((fundId) => shareClassesByFund.has(fundId))
        : Array.from(shareClassesByFund.keys()))
        .filter((fundId) => uniqueFundIdsFromFunds.has(fundId))
        .map((fundId) => ({
          fund_id: fundId,
          fund_name: fundsMap.get(fundId)?.fund_name as string,
          fund_name_full: fundsMap.get(fundId)?.fund_name_full as string,
        })),
    [shareClassesByFund, uniqueFundIdsFromFunds, fundsMap, orderedFundIds]
  );

  // Share classes for selected fund
  const selectedFundShareClasses = shareClassesByFund.get(selectedFundId) || [];

  return (
    <div className="space-y-4 rounded-md border border-dashed border-gray-300 p-3">
      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Select Fund
        </label>
        <GenericDropdown
          data={fundsWithShareClasses}
          selected={fundsWithShareClasses.find((f) => f.fund_id === selectedFundId) || null}
          setSelected={(fund) => {
            setSelectedFundId(fund.fund_id);
            // Clear share class if it doesn't belong to the new fund
            if (template_share_class_id) {
              const sc = allShareClasses?.find(
                (s: any) => s.share_class_id === template_share_class_id
              );
              if (sc?.fund_id !== fund.fund_id) {
                setValue('template_share_class_id', '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue('template_share_class_name', undefined, {
                  shouldDirty: false,
                });
                setValue('template_fund_name', undefined, {
                  shouldDirty: false,
                });
                setValue('template_fund_id', undefined, {
                  shouldDirty: false,
                });
              }
            }
          }}
          getDisplayValue={(fund) => `${fund.fund_name} — ${fund.fund_name_full}`}
          label={(() => {
            const f = fundsWithShareClasses.find((f) => f.fund_id === selectedFundId);
            return f ? `${f.fund_name} — ${f.fund_name_full}` : 'Select a fund...';
          })()}
          searchBar
          className="w-96 justify-between bg-white text-sm text-black cursor-pointer items-center rounded-md border-gray-200 data-[hover]:bg-gray-50 border p-2 text-left  data-[open]:bg-gray-50"
          dropdownClassName="max-h-64 overflow-y-auto w-96"
        />
        <p className="mt-1 text-xs text-gray-500">
          Select the fund containing the share class to duplicate

        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Select Share Class to Duplicate <span className="text-red-500">*</span>
        </label>
        {selectedFundId ? (
          <>
            <GenericDropdown
              data={selectedFundShareClasses}
              selected={
                selectedFundShareClasses.find(
                  (sc) => sc.share_class_id === template_share_class_id
                ) || null
              }
              setSelected={(sc) => {
                setValue('template_share_class_id', sc.share_class_id, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue('template_share_class_name', sc.share_class_name_full, { shouldDirty: false });
                setValue('template_fund_name', fundsMap.get(selectedFundId)?.fund_name_full, { shouldDirty: false });
                setValue('template_fund_id', selectedFundId, { shouldDirty: false });
              }}
              getDisplayValue={(sc) => sc.share_class_name_full}
              label={
                selectedFundShareClasses.find((sc) => sc.share_class_id === template_share_class_id)
                  ?.share_class_name_full ?? 'Select a share class...'
              }
              searchBar
              className="w-96 justify-between bg-white text-sm text-black cursor-pointer items-center rounded-md border-gray-200 data-[hover]:bg-gray-50 border p-2 text-left  data-[open]:bg-gray-50"
              dropdownClassName="max-h-64 overflow-y-auto w-96"
            />
            <p className="mt-1 text-xs text-gray-500">
              The KID document structure and formatting will be copied from this share class
            </p>
            {errors.template_share_class_id && (
              <p className="mt-1 text-xs text-red-600">
                {errors.template_share_class_id.message as string}
              </p>
            )}
          </>
        ) : (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <p className=" text-gray-500 text-sm">
              Select a fund first to see available share classes
            </p>
          </div>
        )}
      </div>

    </div>
  );
};

export default DuplicateDocumentSelector;
